/**
 * Utility functions for managing capital accounts
 */

import { accountService } from "../services/api/accountService";

let cachedAccounts = null;
let cacheTimestamp = null;
const CACHE_DURATION = 30000;

const getCachedAccounts = async () => {
  const now = Date.now();
  if (cachedAccounts && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
    return cachedAccounts;
  }
  
  try {
    const accounts = await accountService.getAccounts();
    cachedAccounts = accounts;
    cacheTimestamp = now;
    return accounts;
  } catch (error) {
    console.error("Error fetching accounts:", error);
    return cachedAccounts || [];
  }
};

export const clearAccountCache = () => {
  cachedAccounts = null;
  cacheTimestamp = null;
};

export const getAllAccounts = async () => {
  return await getCachedAccounts();
};

export const getActiveAccounts = async () => {
  try {
    const accounts = await getCachedAccounts();
    return accounts.filter(acc => acc.isActive);
  } catch (error) {
    console.error("Error getting active accounts:", error);
    return [];
  }
};

/**
 * Get account by ID
 */
export const getAccountById = async (accountId) => {
  try {
    const accounts = await getCachedAccounts();
    return accounts.find(acc => acc.id === accountId);
  } catch (error) {
    console.error("Error getting account by ID:", error);
    return null;
  }
};

/**
 * Get accounts by type (Personal or Investor)
 */
export const getAccountsByType = async (type) => {
  try {
    const accounts = await getActiveAccounts();
    return accounts.filter(acc => acc.type === type);
  } catch (error) {
    console.error("Error getting accounts by type:", error);
    return [];
  }
};

/**
 * Get the currently selected account ID from localStorage
 */
export const getSelectedAccountId = () => {
  return localStorage.getItem("selected-account-id") || "All";
};

export const setSelectedAccountId = (accountId) => {
  localStorage.setItem("selected-account-id", accountId);
  window.dispatchEvent(new Event("account-changed"));
};

/**
 * Get account balance from API service
 */
export const getAccountBalance = async (accountId) => {
  try {
    const account = await getAccountById(accountId);
    return account ? parseFloat(account.startingBalance) || 0 : 0;
  } catch (error) {
    console.error("Error getting account balance:", error);
    return 0;
  }
};

/**
 * Get account type from account ID
 */
export const getAccountType = async (accountId) => {
  if (accountId === "All" || accountId === "All Personal" || accountId === "All Investor") {
    return "aggregate";
  }
  const account = await getAccountById(accountId);
  return account ? account.type : null;
};

/**
 * Get equity type string for backward compatibility
 * Maps account type to old equity type format
 */
export const getEquityTypeFromAccount = (accountId, accounts = null) => {
  // If accounts array is provided, use it (from API)
  if (accounts && accounts.length > 0) {
    const account = accounts.find(acc => acc.id === accountId);
    if (account) {
      return account.type === "Personal" ? "Personal Capital" : "Investor Capital";
    }
  }
  
  // Fallback to localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const localStorageAccounts = user.capitalAccounts || [];
  const account = localStorageAccounts.find(acc => acc.id === accountId);
  if (!account) return null;
  return account.type === "Personal" ? "Personal Capital" : "Investor Capital";
};

/**
 * Get account ID from old equity type format
 * For backward compatibility during migration
 */
export const getAccountIdFromEquityType = async (equityType) => {
  if (equityType === "Personal Capital") {
    const personalAccounts = await getAccountsByType("Personal");
    return personalAccounts.length > 0 ? personalAccounts[0].id : "pc-1";
  } else if (equityType === "Investor Capital") {
    const investorAccounts = await getAccountsByType("Investor");
    return investorAccounts.length > 0 ? investorAccounts[0].id : "ic-1";
  }
  return "All";
};

/**
 * Check if account ID represents an aggregate view
 */
export const isAggregateView = (accountId) => {
  return accountId === "All" || accountId === "All Personal" || accountId === "All Investor";
};

/**
 * Get account IDs included in the current selection
 * For aggregate views, returns all relevant account IDs
 */
export const getIncludedAccountIds = async (accountId) => {
  if (accountId === "All") {
    return (await getActiveAccounts()).map(acc => acc.id);
  } else if (accountId === "All Personal") {
    return (await getAccountsByType("Personal")).map(acc => acc.id);
  } else if (accountId === "All Investor") {
    return (await getAccountsByType("Investor")).map(acc => acc.id);
  } else {
    return [accountId];
  }
};

/**
 * Calculate current balance for an account
 * currentBalance = startingBalance + deposits - withdrawals - payouts + trade P&L
 * For funded accounts, this represents the live account balance
 */
export const calculateCurrentBalance = (account, trades = []) => {
  if (!account) return 0;

  let balance = parseFloat(account.startingBalance) || 0;

  const transactions = account.transactions || [];
  transactions.forEach(txn => {
    if (txn.type === 'deposit') {
      balance += parseFloat(txn.amount) || 0;
    } else if (txn.type === 'withdrawal' || txn.type === 'payout') {
      balance -= parseFloat(txn.amount) || 0;
    }
  });

  const accountTrades = trades.filter(t => t.accountId === account.id);
  accountTrades.forEach(trade => {
    if (account.type === 'Investor') {
      balance += parseFloat(trade.profitLossDollarInvestor || 0);
      balance += parseFloat(trade.feeIC || 0);
      balance += parseFloat(trade.swapIC || 0);
    } else {
      balance += parseFloat(trade.profitLossDollarPersonal || 0);
      balance += parseFloat(trade.feePC || 0);
      balance += parseFloat(trade.swapPC || 0);
    }
  });

  return balance;
};

/**
 * Calculate total payouts for an account
 */
export const calculateTotalPayouts = (account) => {
  if (!account || !account.transactions) return 0;
  
  return account.transactions
    .filter(txn => txn.type === 'payout')
    .reduce((sum, txn) => sum + (parseFloat(txn.amount) || 0), 0);
};

/**
 * Calculate lifetime earnings for a funded account
 * lifetimeEarnings = currentBalance - originalBalance + totalPayouts
 */
export const calculateLifetimeEarnings = (account, trades = []) => {
  if (!account || !account.originalBalance) return 0;
  
  const currentBalance = calculateCurrentBalance(account, trades);
  const originalBalance = parseFloat(account.originalBalance) || 0;
  const totalPayouts = calculateTotalPayouts(account);
  
  return currentBalance - originalBalance + totalPayouts;
};

/**
 * Calculate current cycle earnings (since last payout)
 * cycleEarnings = currentBalance - originalBalance
 */
export const calculateCycleEarnings = (account, trades = []) => {
  if (!account || !account.originalBalance) return 0;
  
  const currentBalance = calculateCurrentBalance(account, trades);
  const originalBalance = parseFloat(account.originalBalance) || 0;
  
  return currentBalance - originalBalance;
};

/**
 * Get the last payout date for an account
 */
export const getLastPayoutDate = (account) => {
  if (!account || !account.transactions) return null;
  
  const payouts = account.transactions
    .filter(txn => txn.type === 'payout')
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  
  return payouts.length > 0 ? payouts[0].date : null;
};

/**
 * Calculate account metrics for display
 */
export const calculateAccountMetrics = (account, trades = []) => {
  if (!account) {
    return {
      currentBalance: 0,
      startingBalance: 0,
      originalBalance: 0,
      lifetimeEarnings: 0,
      lifetimeReturn: 0,
      cycleEarnings: 0,
      cycleReturn: 0,
      totalPayouts: 0,
      lastPayoutDate: null,
    };
  }

  const currentBalance = calculateCurrentBalance(account, trades);
  const originalBalance = parseFloat(account.originalBalance) || parseFloat(account.startingBalance) || 0;
  const lifetimeEarnings = calculateLifetimeEarnings(account, trades);
  const cycleEarnings = calculateCycleEarnings(account, trades);
  const totalPayouts = calculateTotalPayouts(account);
  const lastPayoutDate = getLastPayoutDate(account);

  return {
    currentBalance,
    startingBalance: parseFloat(account.startingBalance) || 0,
    originalBalance,
    lifetimeEarnings,
    lifetimeReturn: originalBalance > 0 ? (lifetimeEarnings / originalBalance) * 100 : 0,
    cycleEarnings,
    cycleReturn: originalBalance > 0 ? (cycleEarnings / originalBalance) * 100 : 0,
    totalPayouts,
    lastPayoutDate,
  };
};
